import { Box, Button, Divider, Paper, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ColorPicker from "../components/ui/color-picker";
import React from "react";

import Topbar from "../components/topbar";
import { useToast } from "../providers/toaster-provider";
import colorPresetsData from '../files/json/color-presets.json';
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import ColorSchemePreview from "../components/color-scheme-preview";

// Type definitions for color presets
type ColorPresetColors = {
    pageBackgroundColor: string;
    containerBackgroundColor: string;
    defaultTextColor: string;
    provisionsTextColor: string;
    transactionsTextColor: string;
    negativeAmountColor: string;
    primaryColor: string;
    endOfHorizonBackgroundColor: string;
    endOfHorizonTextColor: string;
    filterResultBackgroundColor: string;
    filterResultTextColor: string;
    runOutOfMoneyBackgroundColor: string;
    currentDayBackgroundColor: string;
    simulationRowBackgroundColor: string;
    transactionRowBackgroundColor: string;
    provisionRowBackgroundColor: string;
    cashflowIndicatorNegativeBackgroundColor: string;
    cashflowIndicatorNegativeTextColor: string;
    cashflowIndicatorPositiveBackgroundColor: string;
    cashflowIndicatorPositiveTextColor: string;
    oneTimeProvisionBackgroundColor: string;
    oneTimeProvisionTextColor: string;
    recurringProvisionBackgroundColor: string;
    recurringProvisionTextColor: string;
    invoiceProvisionBackgroundColor: string;
    invoiceProvisionTextColor: string;
    payrollProvisionBackgroundColor: string;
    payrollProvisionTextColor: string;
};

type ColorPreset = {
    name: string;
    colors: ColorPresetColors;
};

type ColorPresetsData = {
    [key: string]: ColorPreset;
};

// Color presets imported from JSON
const colorPresets: ColorPresetsData = colorPresetsData;

const ColorSchemeSettings: React.FC = () => {
    const { showSuccess, showError } = useToast();

    // Color scheme states
    const [pageBackgroundColor, setPageBackgroundColor] = React.useState<string>("#FFFFFF");
    const [containerBackgroundColor, setContainerBackgroundColor] = React.useState<string>("#E0E0E0");
    const [defaultTextColor, setDefaultTextColor] = React.useState<string>("#000000");
    const [provisionsTextColor, setProvisionsTextColor] = React.useState<string>("#FF00BF");
    const [transactionsTextColor, setTransactionsTextColor] = React.useState<string>("#000000");
    const [negativeAmountColor, setNegativeAmountColor] = React.useState<string>("#d32f2f");
    const [primaryColor, setPrimaryColor] = React.useState<string>("#00B69B");
    const [endOfHorizonBackgroundColor, setEndOfHorizonBackgroundColor] = React.useState<string>("#ffa2a2");
    const [endOfHorizonTextColor, setEndOfHorizonTextColor] = React.useState<string>("#cc0000");
    const [filterResultBackgroundColor, setFilterResultBackgroundColor] = React.useState<string>("#ffda9d");
    const [filterResultTextColor, setFilterResultTextColor] = React.useState<string>("#cc7700");
    const [runOutOfMoneyBackgroundColor, setRunOutOfMoneyBackgroundColor] = React.useState<string>("#FFEBEE");
    const [currentDayBackgroundColor, setCurrentDayBackgroundColor] = React.useState<string>("#E3F2FD");
    const [simulationRowBackgroundColor, setSimulationRowBackgroundColor] = React.useState<string>("#FAFAFA");
    const [transactionRowBackgroundColor, setTransactionRowBackgroundColor] = React.useState<string>("#DAE1F5");
    const [provisionRowBackgroundColor, setProvisionRowBackgroundColor] = React.useState<string>("#FFFFFF");
    const [cashflowIndicatorNegativeBackgroundColor, setCashflowIndicatorNegativeBackgroundColor] = React.useState<string>("#d32f2f");
    const [cashflowIndicatorNegativeTextColor, setCashflowIndicatorNegativeTextColor] = React.useState<string>("#A80000");
    const [cashflowIndicatorPositiveBackgroundColor, setCashflowIndicatorPositiveBackgroundColor] = React.useState<string>("#388E3C");
    const [cashflowIndicatorPositiveTextColor, setCashflowIndicatorPositiveTextColor] = React.useState<string>("#0D6832");
    const [oneTimeProvisionBackgroundColor, setOneTimeProvisionBackgroundColor] = React.useState<string>("#FFFFFF");
    const [oneTimeProvisionTextColor, setOneTimeProvisionTextColor] = React.useState<string>("#FF00BF");
    const [recurringProvisionBackgroundColor, setRecurringProvisionBackgroundColor] = React.useState<string>("#E8F0FE");
    const [recurringProvisionTextColor, setRecurringProvisionTextColor] = React.useState<string>("#FF00BF");
    const [invoiceProvisionBackgroundColor, setInvoiceProvisionBackgroundColor] = React.useState<string>("#E6F7F4");
    const [invoiceProvisionTextColor, setInvoiceProvisionTextColor] = React.useState<string>("#00897B");
    const [payrollProvisionBackgroundColor, setPayrollProvisionBackgroundColor] = React.useState<string>("#FFF3E0");
    const [payrollProvisionTextColor, setPayrollProvisionTextColor] = React.useState<string>("#EF6C00");
    const [appliedPreset, setAppliedPreset] = React.useState<string | null>('Default');
    // UI-only for now - not yet persisted or wired to any actual text
    // scaling (most of the app's text sizes are hardcoded px, not a
    // rem-based theme, so genuinely scaling them is a larger follow-up).
    const [fontSizePreset, setFontSizePreset] = React.useState<'small' | 'medium' | 'large'>('medium');

    // Helper function to create a color setter that clears the applied preset
    const makeColorSetter = (setter: React.Dispatch<React.SetStateAction<string>>) => {
        return (color: string) => {
            setAppliedPreset(null);
            setter(color);
        };
    };

    const handlePageBackgroundColorChange = makeColorSetter(setPageBackgroundColor);
    const handleContainerBackgroundColorChange = makeColorSetter(setContainerBackgroundColor);
    const handleDefaultTextColorChange = makeColorSetter(setDefaultTextColor);
    const handleProvisionsTextColorChange = makeColorSetter(setProvisionsTextColor);
    const handleTransactionsTextColorChange = makeColorSetter(setTransactionsTextColor);
    const handleNegativeAmountColorChange = makeColorSetter(setNegativeAmountColor);
    const handlePrimaryColorChange = makeColorSetter(setPrimaryColor);
    const handleEndOfHorizonBackgroundColorChange = makeColorSetter(setEndOfHorizonBackgroundColor);
    const handleEndOfHorizonTextColorChange = makeColorSetter(setEndOfHorizonTextColor);
    const handleFilterResultBackgroundColorChange = makeColorSetter(setFilterResultBackgroundColor);
    const handleFilterResultTextColorChange = makeColorSetter(setFilterResultTextColor);
    const handleRunOutOfMoneyBackgroundColorChange = makeColorSetter(setRunOutOfMoneyBackgroundColor);
    const handleCurrentDayBackgroundColorChange = makeColorSetter(setCurrentDayBackgroundColor);
    const handleSimulationRowBackgroundColorChange = makeColorSetter(setSimulationRowBackgroundColor);
    const handleTransactionRowBackgroundColorChange = makeColorSetter(setTransactionRowBackgroundColor);
    const handleProvisionRowBackgroundColorChange = makeColorSetter(setProvisionRowBackgroundColor);
    const handleCashflowIndicatorNegativeBackgroundColorChange = makeColorSetter(setCashflowIndicatorNegativeBackgroundColor);
    const handleCashflowIndicatorNegativeTextColorChange = makeColorSetter(setCashflowIndicatorNegativeTextColor);
    const handleCashflowIndicatorPositiveBackgroundColorChange = makeColorSetter(setCashflowIndicatorPositiveBackgroundColor);
    const handleCashflowIndicatorPositiveTextColorChange = makeColorSetter(setCashflowIndicatorPositiveTextColor);
    const handleOneTimeProvisionBackgroundColorChange = makeColorSetter(setOneTimeProvisionBackgroundColor);
    const handleOneTimeProvisionTextColorChange = makeColorSetter(setOneTimeProvisionTextColor);
    const handleRecurringProvisionBackgroundColorChange = makeColorSetter(setRecurringProvisionBackgroundColor);
    const handleRecurringProvisionTextColorChange = makeColorSetter(setRecurringProvisionTextColor);
    const handleInvoiceProvisionBackgroundColorChange = makeColorSetter(setInvoiceProvisionBackgroundColor);
    const handleInvoiceProvisionTextColorChange = makeColorSetter(setInvoiceProvisionTextColor);
    const handlePayrollProvisionBackgroundColorChange = makeColorSetter(setPayrollProvisionBackgroundColor);
    const handlePayrollProvisionTextColorChange = makeColorSetter(setPayrollProvisionTextColor);

    const applyColorPreset = (preset: typeof colorPresets.highContrast) => {
        setAppliedPreset(preset.name);
        setPageBackgroundColor(preset.colors.pageBackgroundColor);
        setContainerBackgroundColor(preset.colors.containerBackgroundColor);
        setDefaultTextColor(preset.colors.defaultTextColor);
        setProvisionsTextColor(preset.colors.provisionsTextColor);
        setTransactionsTextColor(preset.colors.transactionsTextColor);
        setNegativeAmountColor(preset.colors.negativeAmountColor);
        setPrimaryColor(preset.colors.primaryColor);
        setEndOfHorizonBackgroundColor(preset.colors.endOfHorizonBackgroundColor);
        setEndOfHorizonTextColor(preset.colors.endOfHorizonTextColor);
        setFilterResultBackgroundColor(preset.colors.filterResultBackgroundColor);
        setFilterResultTextColor(preset.colors.filterResultTextColor);
        setRunOutOfMoneyBackgroundColor(preset.colors.runOutOfMoneyBackgroundColor);
        setCurrentDayBackgroundColor(preset.colors.currentDayBackgroundColor);
        setSimulationRowBackgroundColor(preset.colors.simulationRowBackgroundColor);
        setTransactionRowBackgroundColor(preset.colors.transactionRowBackgroundColor);
        setProvisionRowBackgroundColor(preset.colors.provisionRowBackgroundColor);
        setCashflowIndicatorNegativeBackgroundColor(preset.colors.cashflowIndicatorNegativeBackgroundColor);
        setCashflowIndicatorNegativeTextColor(preset.colors.cashflowIndicatorNegativeTextColor);
        setCashflowIndicatorPositiveBackgroundColor(preset.colors.cashflowIndicatorPositiveBackgroundColor);
        setCashflowIndicatorPositiveTextColor(preset.colors.cashflowIndicatorPositiveTextColor);
        setOneTimeProvisionBackgroundColor(preset.colors.oneTimeProvisionBackgroundColor);
        setOneTimeProvisionTextColor(preset.colors.oneTimeProvisionTextColor);
        setRecurringProvisionBackgroundColor(preset.colors.recurringProvisionBackgroundColor);
        setRecurringProvisionTextColor(preset.colors.recurringProvisionTextColor);
        setInvoiceProvisionBackgroundColor(preset.colors.invoiceProvisionBackgroundColor);
        setInvoiceProvisionTextColor(preset.colors.invoiceProvisionTextColor);
        setPayrollProvisionBackgroundColor(preset.colors.payrollProvisionBackgroundColor);
        setPayrollProvisionTextColor(preset.colors.payrollProvisionTextColor);
        showSuccess(`Applied ${preset.name} preset`);
    };

    const { user } = useUser();
    const [fetchColorScheme, setFetchColorScheme] = useApi();
    const [saveColorScheme, setSaveColorScheme] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Color Scheme";
    }, []);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.colorScheme.fetch, [
            { param: 'entityId', value: user.entityId },
        ]);
        setFetchColorScheme({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId]);

    React.useEffect(() => {
        const { data, isLoading } = fetchColorScheme;
        const colors: Partial<ColorPresetColors> | undefined = data?.colors;
        if (!isLoading && colors) {
            if (colors.pageBackgroundColor) setPageBackgroundColor(colors.pageBackgroundColor);
            if (colors.containerBackgroundColor) setContainerBackgroundColor(colors.containerBackgroundColor);
            if (colors.defaultTextColor) setDefaultTextColor(colors.defaultTextColor);
            if (colors.provisionsTextColor) setProvisionsTextColor(colors.provisionsTextColor);
            if (colors.transactionsTextColor) setTransactionsTextColor(colors.transactionsTextColor);
            if (colors.negativeAmountColor) setNegativeAmountColor(colors.negativeAmountColor);
            if (colors.primaryColor) setPrimaryColor(colors.primaryColor);
            if (colors.endOfHorizonBackgroundColor) setEndOfHorizonBackgroundColor(colors.endOfHorizonBackgroundColor);
            if (colors.endOfHorizonTextColor) setEndOfHorizonTextColor(colors.endOfHorizonTextColor);
            if (colors.filterResultBackgroundColor) setFilterResultBackgroundColor(colors.filterResultBackgroundColor);
            if (colors.filterResultTextColor) setFilterResultTextColor(colors.filterResultTextColor);
            if (colors.runOutOfMoneyBackgroundColor) setRunOutOfMoneyBackgroundColor(colors.runOutOfMoneyBackgroundColor);
            if (colors.currentDayBackgroundColor) setCurrentDayBackgroundColor(colors.currentDayBackgroundColor);
            if (colors.simulationRowBackgroundColor) setSimulationRowBackgroundColor(colors.simulationRowBackgroundColor);
            if (colors.transactionRowBackgroundColor) setTransactionRowBackgroundColor(colors.transactionRowBackgroundColor);
            if (colors.provisionRowBackgroundColor) setProvisionRowBackgroundColor(colors.provisionRowBackgroundColor);
            if (colors.cashflowIndicatorNegativeBackgroundColor) setCashflowIndicatorNegativeBackgroundColor(colors.cashflowIndicatorNegativeBackgroundColor);
            if (colors.cashflowIndicatorNegativeTextColor) setCashflowIndicatorNegativeTextColor(colors.cashflowIndicatorNegativeTextColor);
            if (colors.cashflowIndicatorPositiveBackgroundColor) setCashflowIndicatorPositiveBackgroundColor(colors.cashflowIndicatorPositiveBackgroundColor);
            if (colors.cashflowIndicatorPositiveTextColor) setCashflowIndicatorPositiveTextColor(colors.cashflowIndicatorPositiveTextColor);
            if (colors.oneTimeProvisionBackgroundColor) setOneTimeProvisionBackgroundColor(colors.oneTimeProvisionBackgroundColor);
            if (colors.oneTimeProvisionTextColor) setOneTimeProvisionTextColor(colors.oneTimeProvisionTextColor);
            if (colors.recurringProvisionBackgroundColor) setRecurringProvisionBackgroundColor(colors.recurringProvisionBackgroundColor);
            if (colors.recurringProvisionTextColor) setRecurringProvisionTextColor(colors.recurringProvisionTextColor);
            if (colors.invoiceProvisionBackgroundColor) setInvoiceProvisionBackgroundColor(colors.invoiceProvisionBackgroundColor);
            if (colors.invoiceProvisionTextColor) setInvoiceProvisionTextColor(colors.invoiceProvisionTextColor);
            if (colors.payrollProvisionBackgroundColor) setPayrollProvisionBackgroundColor(colors.payrollProvisionBackgroundColor);
            if (colors.payrollProvisionTextColor) setPayrollProvisionTextColor(colors.payrollProvisionTextColor);
            setAppliedPreset(data.appliedPreset ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchColorScheme]);

    React.useEffect(() => {
        const { data, isLoading, error } = saveColorScheme;
        if (data && !isLoading) {
            showSuccess("Color scheme settings updated successfully.");
            // Full reload so every already-mounted page (sidebar, topbar, etc.)
            // picks up the newly saved scheme instead of just this settings page.
            setTimeout(() => window.location.reload(), 800);
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to save color scheme settings.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveColorScheme]);

    const handleSave = () => {
        if (!user?.entityId) return;
        const colors: ColorPresetColors = {
            pageBackgroundColor,
            containerBackgroundColor,
            defaultTextColor,
            provisionsTextColor,
            transactionsTextColor,
            negativeAmountColor,
            primaryColor,
            endOfHorizonBackgroundColor,
            endOfHorizonTextColor,
            filterResultBackgroundColor,
            filterResultTextColor,
            runOutOfMoneyBackgroundColor,
            currentDayBackgroundColor,
            simulationRowBackgroundColor,
            transactionRowBackgroundColor,
            provisionRowBackgroundColor,
            cashflowIndicatorNegativeBackgroundColor,
            cashflowIndicatorNegativeTextColor,
            cashflowIndicatorPositiveBackgroundColor,
            cashflowIndicatorPositiveTextColor,
            oneTimeProvisionBackgroundColor,
            oneTimeProvisionTextColor,
            recurringProvisionBackgroundColor,
            recurringProvisionTextColor,
            invoiceProvisionBackgroundColor,
            invoiceProvisionTextColor,
            payrollProvisionBackgroundColor,
            payrollProvisionTextColor,
        };
        const { url, method } = buildApiRequestConfig(routes.colorScheme.save);
        setSaveColorScheme({ url, method, body: { entityId: user.entityId, colors, appliedPreset } });
    };

    // The colors currently being edited (not yet saved) - fed to the live
    // preview so it reflects in-progress changes, not the last-saved scheme.
    const draftColors: ColorPresetColors = {
        pageBackgroundColor,
        containerBackgroundColor,
        defaultTextColor,
        provisionsTextColor,
        transactionsTextColor,
        negativeAmountColor,
        primaryColor,
        endOfHorizonBackgroundColor,
        endOfHorizonTextColor,
        filterResultBackgroundColor,
        filterResultTextColor,
        runOutOfMoneyBackgroundColor,
        currentDayBackgroundColor,
        simulationRowBackgroundColor,
        transactionRowBackgroundColor,
        provisionRowBackgroundColor,
        cashflowIndicatorNegativeBackgroundColor,
        cashflowIndicatorNegativeTextColor,
        cashflowIndicatorPositiveBackgroundColor,
        cashflowIndicatorPositiveTextColor,
        oneTimeProvisionBackgroundColor,
        oneTimeProvisionTextColor,
        recurringProvisionBackgroundColor,
        recurringProvisionTextColor,
        invoiceProvisionBackgroundColor,
        invoiceProvisionTextColor,
        payrollProvisionBackgroundColor,
        payrollProvisionTextColor,
    };

    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Color Scheme Settings"/>
            <div style={{ padding: '10px' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Paper
                    sx={{
                        background: 'var(--color-page-background)',
                        flex: '1 1 420px',
                        minWidth: 0,
                        maxWidth: '100%',
                        padding: { xs: 2, sm: 4 },
                        boxSizing: 'border-box',
                        borderRadius: 0,
                        position: 'relative',
                    }}
                >
                    <Typography fontSize={14} fontWeight={600} mb={2}>Presets</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {Object.values(colorPresets).map((preset) => {
                            const isApplied = appliedPreset === preset.name;
                            return (
                            <Box
                                key={preset.name}
                                onClick={() => applyColorPreset(preset)}
                                sx={{
                                    cursor: 'pointer',
                                    border: isApplied ? '1.5px solid #00B69B' : '1px solid #CACACA',
                                    borderRadius: 1,
                                    padding: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 120,
                                    transition: 'all 0.2s ease',
                                    backgroundColor: isApplied ? 'rgba(0, 182, 155, 0.05)' : 'transparent',
                                }}
                            >
                                <Typography fontSize={12} fontWeight={600}>{preset.name}</Typography>
                                <Box display="flex" flexWrap="wrap" justifyContent="center">
                                    {[
                                        preset.colors.pageBackgroundColor,
                                        preset.colors.containerBackgroundColor,
                                        preset.colors.defaultTextColor,
                                        preset.colors.negativeAmountColor,
                                        preset.colors.primaryColor,
                                        preset.colors.cashflowIndicatorNegativeBackgroundColor,
                                        preset.colors.cashflowIndicatorPositiveBackgroundColor,
                                    ].map((color, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: color,
                                                border: '1px solid rgba(0,0,0,0.1)',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        );
                        })}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography fontSize={14} fontWeight={600} mb={0.5}>Font Size</Typography>
                    <Typography fontSize={12} sx={{ color: 'var(--color-text-muted)', mb: 2 }}>
                        Coming soon - not yet applied anywhere.
                    </Typography>
                    <ToggleButtonGroup
                        value={fontSizePreset}
                        exclusive
                        size="small"
                        onChange={(_, val) => val && setFontSizePreset(val)}
                        sx={{ height: 35 }}
                    >
                        <ToggleButton value="small" sx={{ textTransform: 'none', fontSize: 12, px: 2, '&.Mui-selected': { background: 'rgba(var(--color-primary-rgb), 0.1333)', color: 'var(--color-primary)' } }}>
                            Small
                        </ToggleButton>
                        <ToggleButton value="medium" sx={{ textTransform: 'none', fontSize: 14, px: 2, '&.Mui-selected': { background: 'rgba(var(--color-primary-rgb), 0.1333)', color: 'var(--color-primary)' } }}>
                            Medium
                        </ToggleButton>
                        <ToggleButton value="large" sx={{ textTransform: 'none', fontSize: 16, px: 2, '&.Mui-selected': { background: 'rgba(var(--color-primary-rgb), 0.1333)', color: 'var(--color-primary)' } }}>
                            Large
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Divider sx={{ my: 2 }} />
                    <Typography fontSize={14} fontWeight={600} mb={2}>Custom Color</Typography>
                    <Box
                        display="grid"
                        sx={{ gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' } }}
                        gap={2}
                        maxWidth={1000}
                    >
                        <ColorPicker
                            label="Page Background Color"
                            value={pageBackgroundColor}
                            onChange={handlePageBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Container Background Color"
                            value={containerBackgroundColor}
                            onChange={handleContainerBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Default Text Color"
                            value={defaultTextColor}
                            onChange={handleDefaultTextColorChange}
                        />
                        <ColorPicker
                            label="Provisions Text Color"
                            value={provisionsTextColor}
                            onChange={handleProvisionsTextColorChange}
                        />
                        <ColorPicker
                            label="Transactions Text Color"
                            value={transactionsTextColor}
                            onChange={handleTransactionsTextColorChange}
                        />
                        <ColorPicker
                            label="Negative Amount Color"
                            value={negativeAmountColor}
                            onChange={handleNegativeAmountColorChange}
                        />
                        <ColorPicker
                            label="Primary Color"
                            value={primaryColor}
                            onChange={handlePrimaryColorChange}
                        />
                        <ColorPicker
                            label="End of Horizon Background Color"
                            value={endOfHorizonBackgroundColor}
                            onChange={handleEndOfHorizonBackgroundColorChange}
                        />
                        <ColorPicker
                            label="End of Horizon Text Color"
                            value={endOfHorizonTextColor}
                            onChange={handleEndOfHorizonTextColorChange}
                        />
                        <ColorPicker
                            label="End of Filter Results Background Color"
                            value={filterResultBackgroundColor}
                            onChange={handleFilterResultBackgroundColorChange}
                        />
                        <ColorPicker
                            label="End of Filter Results Text Color"
                            value={filterResultTextColor}
                            onChange={handleFilterResultTextColorChange}
                        />
                        <ColorPicker
                            label="Run Out of Money Background Color"
                            value={runOutOfMoneyBackgroundColor}
                            onChange={handleRunOutOfMoneyBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Current Day Background Color"
                            value={currentDayBackgroundColor}
                            onChange={handleCurrentDayBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Simulation Row Background Color"
                            value={simulationRowBackgroundColor}
                            onChange={handleSimulationRowBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Real Transaction Row Background Color"
                            value={transactionRowBackgroundColor}
                            onChange={handleTransactionRowBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Provision Row Background Color"
                            value={provisionRowBackgroundColor}
                            onChange={handleProvisionRowBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Cashflow Indicator (Negative) Background Color"
                            value={cashflowIndicatorNegativeBackgroundColor}
                            onChange={handleCashflowIndicatorNegativeBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Cashflow Indicator (Negative) Text Color"
                            value={cashflowIndicatorNegativeTextColor}
                            onChange={handleCashflowIndicatorNegativeTextColorChange}
                        />
                        <ColorPicker
                            label="Cashflow Indicator (Positive) Background Color"
                            value={cashflowIndicatorPositiveBackgroundColor}
                            onChange={handleCashflowIndicatorPositiveBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Cashflow Indicator (Positive) Text Color"
                            value={cashflowIndicatorPositiveTextColor}
                            onChange={handleCashflowIndicatorPositiveTextColorChange}
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography fontSize={14} fontWeight={600} mb={2}>Provision Type Colors</Typography>
                    <Typography fontSize={12} sx={{ color: 'var(--color-text-muted)', mb: 2 }}>
                        Row background/text colors on the Transactions page, per kind of unlinked provision.
                    </Typography>
                    <Box
                        display="grid"
                        sx={{ gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' } }}
                        gap={2}
                        maxWidth={1000}
                    >
                        <ColorPicker
                            label="One-Time Provision Background Color"
                            value={oneTimeProvisionBackgroundColor}
                            onChange={handleOneTimeProvisionBackgroundColorChange}
                        />
                        <ColorPicker
                            label="One-Time Provision Text Color"
                            value={oneTimeProvisionTextColor}
                            onChange={handleOneTimeProvisionTextColorChange}
                        />
                        <ColorPicker
                            label="Recurring Provision Background Color"
                            value={recurringProvisionBackgroundColor}
                            onChange={handleRecurringProvisionBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Recurring Provision Text Color"
                            value={recurringProvisionTextColor}
                            onChange={handleRecurringProvisionTextColorChange}
                        />
                        <ColorPicker
                            label="Invoice Provision Background Color"
                            value={invoiceProvisionBackgroundColor}
                            onChange={handleInvoiceProvisionBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Invoice Provision Text Color"
                            value={invoiceProvisionTextColor}
                            onChange={handleInvoiceProvisionTextColorChange}
                        />
                        <ColorPicker
                            label="Payroll Provision Background Color"
                            value={payrollProvisionBackgroundColor}
                            onChange={handlePayrollProvisionBackgroundColorChange}
                        />
                        <ColorPicker
                            label="Payroll Provision Text Color"
                            value={payrollProvisionTextColor}
                            onChange={handlePayrollProvisionTextColorChange}
                        />
                    </Box>
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ color: '#fff' }}
                            disabled={saveColorScheme.isLoading}
                            onClick={handleSave}
                        >
                            {saveColorScheme.isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </Paper>
                <Box sx={{ position: 'sticky', top: 16, flex: '1 1 320px', minWidth: 0, maxWidth: 480 }}>
                    <ColorSchemePreview colors={draftColors} />
                </Box>
                </Box>
            </div>
        </div>
    );
};

export default ColorSchemeSettings;
